import React, { useEffect, useReducer, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEnvelope, FaLock, FaArrowRight, FaUserShield } from 'react-icons/fa';

import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';
import './Login.css';

const AdminLogin = () => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const { currentUser, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/admin';

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.is_staff || currentUser.is_superuser)) {
      navigate('/admin', { replace: true });
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      forceUpdate();
    }, 50);

    return () => clearTimeout(timer);
  }, [errors]);

  const validateForm = () => {
    const newErrors = { email: '', password: '', general: '' };
    let isValid = true;

    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    if (submitting) {
      return false;
    }

    toast.dismiss();
    setErrors({ email: '', password: '', general: '' });
    setSubmitting(true);

    if (!validateForm()) {
      setSubmitting(false);
      return false;
    }

    const loginTimeout = setTimeout(() => {
      if (submitting) {
        toast.info('Login is taking longer than expected. Please wait...');
      }
    }, 5000);

    try {
      const user = await login(email, password);

      if (!user) {
        throw new Error('No user data returned from login');
      }

      const isAdminOrStaff = user.role === 'admin' || user.is_staff || user.is_superuser;
      if (!isAdminOrStaff) {
        await logout();
        const msg = 'This account does not have admin access.';
        setErrors({ email: '', password: '', general: msg });
        toast.error(msg);
        setSubmitting(false);
        clearTimeout(loginTimeout);
        return false;
      }

      toast.success('Admin login successful! Redirecting...');
      clearTimeout(loginTimeout);

      setTimeout(() => {
        navigate(from, { replace: true });
      }, 500);

      return true;
    } catch (err) {
      let errorMessage = 'Failed to log in. Please check your credentials and try again.';
      const fieldErrors = {};

      if (err.response?.data) {
        const { data } = err.response;

        if (data.email) {
          fieldErrors.email = Array.isArray(data.email) ? data.email[0] : data.email;
        }
        if (data.password) {
          fieldErrors.password = Array.isArray(data.password) ? data.password[0] : data.password;
        }
        if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        } else if (data.detail) {
          errorMessage = data.detail;
        }
      } else if (err.message) {
        if (
          err.message.includes('Invalid credentials') ||
          err.message.includes('No active account') ||
          err.message.includes('Incorrect email or password')
        ) {
          errorMessage = 'Invalid email or password';
          fieldErrors.email = 'Invalid email or password';
          fieldErrors.password = 'Invalid email or password';
        } else {
          errorMessage = err.message;
        }
      }

      const newErrors = {
        ...fieldErrors,
        general: Object.keys(fieldErrors).length === 0 ? errorMessage : '',
      };

      setErrors(newErrors);

      if (newErrors.general) {
        toast.error(newErrors.general);
      } else if (Object.keys(fieldErrors).length > 0) {
        toast.error('Please check the form for errors');
      }

      setSubmitting(false);
      clearTimeout(loginTimeout);
      return false;
    }
  };

  return (
    <Layout navbarVariant="minimal" showFooter={false}>
      <div className="login-page-container">
        <div className="login-split-screen">
          <div className="login-left-panel">
            <div className="login-form-container">
              <img src={logo} alt="UniTrade Logo" className="login-logo" />
              <h2>
                <FaUserShield className="me-2" />
                Admin Login
              </h2>
              <p className="login-subtitle">Login to continue to Admin Dashboard</p>

              <div
                className="error-container"
                key={`error-${Date.now()}`}
                style={{
                  minHeight: '24px',
                  marginBottom: '1rem',
                  display: errors.general ? 'block' : 'none',
                  opacity: errors.general ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                {errors.general && (
                  <div
                    className="error-message"
                    role="alert"
                    aria-live="assertive"
                    style={{
                      display: 'block',
                      margin: '0 0 1rem 0',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#fef2f2',
                      color: '#b91c1c',
                      border: '1px solid #fecaca',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                    }}
                  >
                    {errors.general}
                  </div>
                )}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(e);
                }}
                className="login-form"
                noValidate
              >
                <div className="input-group-login">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    placeholder="Admin Email Address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) {
                        setErrors((prev) => ({ ...prev, email: '' }));
                      }
                    }}
                    className={errors.email ? 'input-error' : ''}
                    required
                  />
                  {errors.email && (
                    <div
                      className="field-error"
                      style={{
                        color: '#b91c1c',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        marginLeft: '2.5rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#fef2f2',
                        borderRadius: '0.25rem',
                        display: 'block',
                        width: 'calc(100% - 2.5rem)',
                      }}
                    >
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="input-group-login">
                  <FaLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors((prev) => ({ ...prev, password: '' }));
                      }
                    }}
                    className={errors.password ? 'input-error' : ''}
                    required
                  />
                  {errors.password && (
                    <div
                      className="field-error"
                      style={{
                        color: '#b91c1c',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        marginLeft: '2.5rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#fef2f2',
                        borderRadius: '0.25rem',
                        display: 'block',
                        width: 'calc(100% - 2.5rem)',
                      }}
                    >
                      {errors.password}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={submitting}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmit();
                    }}
                  >
                    {submitting ? 'Logging in...' : 'Login'}
                  </button>

                  <div className="text-center mt-3">
                    <Link to="/forgot-password" className="forgot-password-link">
                      Forgot your password?
                    </Link>
                  </div>

                  <div className="text-center mt-3">
                    <Link to="/login" className="forgot-password-link">
                      <FaArrowRight className="me-2" />
                      User login
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="login-right-panel">
            <div className="cta-content">
              <FaArrowRight className="cta-icon" />
              <h2>Admin Access</h2>
              <p>Use your admin credentials to manage users, products, orders and platform settings.</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminLogin;
